import { environment } from './../../environments/environment';
import { ChangeUserPassDto } from './../models/dto/change-user-pass.dto';
import { OrganizationsDto } from './../models/dto/organization.dto';
import { CustomerDto } from './../models/dto/customer.dto';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CountryItemDto } from '../models/dto/country-item.dto';
import { CreateUpdateUserDto } from '../models/dto/create-update-user.dto';
import { PermissionDto } from '../models/dto/permission.dto';
import { UserDto } from '../models/dto/user.dto';
import { ConfirmUserOrRestorePassword } from '../models/dto/confirm-user-restore-pass.dto';
import { SummaryReportDto } from '../models/dto/summary-report.dto';
import { CreatePermissionDto } from '../models/dto/create-permissions.dto';
import { FileName } from '../models/file-name';
import { TwoFactorAuthenticationDto } from '../models/dto/2fa.dto';
import { GetAuthenticatorForUserDto } from '../models/dto/auth-for-user.dto';
import { RecoveryCodesDto } from '../models/dto/recovery-code.dto';
import { CustomerOrganizationDto } from '../models/dto/customer-organization.dto';
import { CustomerAndOrganizations, OrganizationModel } from '../models/customer-and-organizations.model';
import { map } from 'rxjs/operators';
import { AnalysisTypeDto } from '../models/dto/analysis-type.dto';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private blobOptions = {
    observe: 'response' as 'body',
    responseType: 'blob' as 'json'
  };

  constructor(private http: HttpClient) { }

  public forgotPassword(email: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/forgot_password`;
    const dto = {
      email: email
    }
    return this.http.post(urlApi, dto);
  }

  public restorePassword(id: string, restoreDto: ConfirmUserOrRestorePassword): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/${id}/restore_password`;
    return this.http.post(urlApi, restoreDto);
  }

  public confirmUser(id: string, confirmDto: ConfirmUserOrRestorePassword): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/${id}/confirm`;
    return this.http.post(urlApi, confirmDto);
  }

  public changePassword(changePassDto: ChangeUserPassDto): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/change_password`;
    return this.http.put(urlApi, changePassDto);
  }

  public getUserProfile(): Observable<UserDto> {
    const urlApi = `${environment.apiUrl}/api/users/profile`;
    return this.http.get<UserDto>(urlApi);
  }

  public changeLocale(id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/locale/${id}`;
    return this.http.put(urlApi, null);
  }

  public getCountriesList(): Observable<CountryItemDto[]> {
    const urlApi = `${environment.apiUrl}/api/countries`;
    return this.http.get<CountryItemDto[]>(urlApi);
  }
  // TODO: Add HttpService for fetching available markets, time zones and cultures. Maybe these should all go in the same API call together with countries?
  //       The list of available markets/time zones/cultures and countries should also be limited to the ones that are currently available and no more.

  ////////////////////////////// USER //////////////////////

  public createUser(user: CreateUpdateUserDto): Observable<string> {
    const urlApi = `${environment.apiUrl}/api/users`;
    return this.http.post<string>(urlApi, user);
  }

  public updateUser(id: string, user: CreateUpdateUserDto): Observable<string> {
    const urlApi = `${environment.apiUrl}/api/users/${id}`;
    return this.http.put<string>(urlApi, user);
  }

  public deleteUser(id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/${id}`;
    return this.http.delete(urlApi);
  }

  public isTwoFactorAuthenticationEnabled(): Observable<TwoFactorAuthenticationDto> {
    const urlApi = `${environment.apiUrl}/api/users/istwofactorauthenticationenabled`;
    return this.http.post<TwoFactorAuthenticationDto>(urlApi, null);
  }

  public getAuthenticatorForUser(): Observable<GetAuthenticatorForUserDto> {
    const urlApi = `${environment.apiUrl}/api/users/getauthenticatorforuser`;
    return this.http.post<GetAuthenticatorForUserDto>(urlApi, null);
  }

  public enableAuthenticatorForUser(code: string): Observable<RecoveryCodesDto> {
    const urlApi = `${environment.apiUrl}/api/users/enableauthenticatorforuser?code=${code}`;
    return this.http.post<RecoveryCodesDto>(urlApi, null);
  }

  public resetTwoFactorAuthenticationForUser(userId: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/resettwofactorauthenticationforuser/${userId}`;
    return this.http.post<any>(urlApi, null);
  }

  ////////////////////////////// CUSTOMER //////////////////////

  public getCustomerList(): Observable<CustomerDto[]> {
    const urlApi = `${environment.apiUrl}/api/customers?sort=name`;
    return this.http.get<CustomerDto[]>(urlApi);
  }

  public createCustomer(customer: CustomerDto): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers`;
    return this.http.post<any>(urlApi, customer);
  }

  public updateCustomer(id: string, dto: any): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${id}`;
    return this.http.put<any>(urlApi, dto);
  }

  public deleteCustomer(id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${id}`;
    return this.http.delete<any>(urlApi);
  }

  ////////////////////////////// ORGANIZATION //////////////////////

  public createOrganization(id: string, org: OrganizationsDto): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${id}/organizationunits`;
    return this.http.post<any>(urlApi, org, { observe: 'response' });
  }

  public getOrganizationsByCustomerId(id: string): Observable<OrganizationsDto[]> {
    const urlApi = `${environment.apiUrl}/api/customers/${id}/organizationunits/`;
    return this.http.get<OrganizationsDto[]>(urlApi);
  }

  public getOrganizationById(customerId: string, id: string): Observable<OrganizationsDto> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}`;
    return this.http.get<OrganizationsDto>(urlApi);
  }

  public updateOrganizationById(id: string, customerId: string, dto: any): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}`;
    return this.http.put<any>(urlApi, dto);
  }

  public deleteOrganizationById(customerId: string, id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}`;
    return this.http.delete<any>(urlApi);
  }

  public getOrganizationSettings(customerId: string, id: string, settingsType: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}/settings?settingsType=${settingsType}`;
    return this.http.get<any>(urlApi);
  }

  public getAllOrganizationSettings(customerId: string, id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}/settings/all`;
    return this.http.get<any>(urlApi);
  }

  public updateOrganizationSettings(customerId: string, id: string, settingsType: string, dto: any): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}/settings?settingsType=${settingsType}`;
    return this.http.post<any>(urlApi, dto);
  }

  public deleteOrganizationSettings(customerId: string, id: string, settingsType: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/organizationunits/${id}/settings?settingsType=${settingsType}`;
    return this.http.delete<any>(urlApi);
  }

  public getAllSettings(): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/settings`;
    return this.http.get<any>(urlApi);
  }

  public getAllOrganizations(): Observable<CustomerAndOrganizations[]> {
    const urlApi = `${environment.apiUrl}/api/customers/organizations`;
    const organizations = this.http.get<CustomerOrganizationDto[]>(urlApi)
      .pipe(map((resp: CustomerOrganizationDto[]) => {
        const transformed: CustomerAndOrganizations[] = [];
        for (const item of resp) {
          let customer = transformed.find(e => e.customerId === item.customerId);
          if (customer) {
            const organization = new OrganizationModel();
            organization.organizationId = item.organizationId;
            organization.organizationName = item.organizationName;
            customer.organizations.push(organization);
          } else {
            const organization = new OrganizationModel();
            organization.organizationId = item.organizationId;
            organization.organizationName = item.organizationName;
            customer = new CustomerAndOrganizations();
            customer.customerId = item.customerId;
            customer.customerName = item.customerName;
            customer.organizations.push(organization);
            transformed.push(customer);
          }
        }
        return transformed;
      }));
    return organizations
  }

  //////////////////////////////////////////////////////////////
  public getDtoByUrl(url: string): Observable<any> {
    return this.http.get<any>(url);
  }

  ////////////////////////////// PERMISSION //////////////////////

  public getPermission(): Observable<string[]> {
    const urlApi = `${environment.apiUrl}/api/permissions`;
    return this.http.get<string[]>(urlApi);
  }

  public getPermissionById(id: string): Observable<PermissionDto[]> {
    const urlApi = `${environment.apiUrl}/api/users/${id}/permissions`;
    return this.http.get<PermissionDto[]>(urlApi);
  }

  public createPermissionById(id: string, permission: CreatePermissionDto): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/${id}/permissions`;
    return this.http.post<any>(urlApi, permission);
  }

  public deletePermissionById(id: string, permissionId: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/users/${id}/permissions/${permissionId}`;
    return this.http.delete<any>(urlApi);
  }

  ////////////////////// REQUESTS //////////////////////////

  public createAnalysisRequest(files: File[], fileName: FileName): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/analysisrequests`;
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file, file.name);
    }
    formData.append('patientId', fileName.patientId);
    formData.append('analysisId', fileName.analysisId);
    return this.http.post<any>(urlApi, formData, { reportProgress: true, observe: 'events' });
  }

  public retriggerRequest(id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/analysisrequests/re-trigger/${id}`;
    return this.http.post<any>(urlApi, null);
  }

  public getAnalysisQueue(): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/analysisrequests/queue`;
    return this.http.get<any>(urlApi);
  }

  ////////////////////////////// FILES //////////////////////

  public getReportFile(id: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/analysisreports/${id}`;
    return this.http.get<any>(urlApi, this.blobOptions);
  }

  public getFileByName(fileName: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/analysisreports/getfilebyname?fileName=${fileName}`;
    return this.http.get<any>(urlApi, this.blobOptions);
  }

  public getActivityLogs(startDate: string, endDate: string): Observable<any> {
    const urlApi = `${environment.apiUrl}/api/activitylogs/report?startDate=${startDate}&endDate=${endDate}`;
    return this.http.get<any>(urlApi, this.blobOptions);
  }

  public getSummaryReport(customerId: string, startDate: string, endDate: string): Observable<SummaryReportDto> {
    let params = new HttpParams();
    if (startDate) { params = params.set('startDate', startDate); }
    if (endDate) { params = params.set('endDate', endDate); }
    const urlApi = `${environment.apiUrl}/api/customers/${customerId}/reports/summary`;
    return this.http.get<SummaryReportDto>(urlApi, { params });
  }

  public getAnalysisTypesForUser(): Promise<AnalysisTypeDto[]> {
    return new Promise((resolve, reject) => {
      const urlApi = `${environment.apiUrl}/api/analysistypes/customer-analysis-types`;
      this.http.get<AnalysisTypeDto[]>(urlApi).subscribe({
        next: (resp: AnalysisTypeDto[]) => { resolve(resp); },
        error: (error) => reject(error)
      });
    });
  }

  public getAllAnalysisTypes(): Promise<AnalysisTypeDto[]> {
    return new Promise((resolve, reject) => {
      const urlApi = `${environment.apiUrl}/api/analysistypes`;
      this.http.get<AnalysisTypeDto[]>(urlApi).subscribe({
        next: (resp: AnalysisTypeDto[]) => { resolve(resp); },
        error: (error) => reject(error)
      });
    });
  }

  public getCustomerAnalysisTypes(customerId: string): Promise<AnalysisTypeDto[]> {
    return new Promise((resolve, reject) => {
      const urlApi = `${environment.apiUrl}/api/customers/${customerId}/analysis-types`;
      this.http.get<AnalysisTypeDto[]>(urlApi).subscribe({
        next: (resp: AnalysisTypeDto[]) => { resolve(resp); },
        error: (error) => reject(error)
      });
    });
  }
}
